import { take, map, filter } from 'rxjs/operators';

import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { Subscription } from 'rxjs';

import { AppState } from 'src/app';
import { selectIsUsa } from 'src/app/state/selectors/current-contract.selectors';
import { BootstrapService, MarketService } from 'src/core';
import { SharedSolaceStreamService } from 'src/solace';
import { IndexRow } from './indexRow.class';

const enum ORIENTATIONS {
  vertical = 'vertical',
  horizontal = 'horizontal',
}

@Component({
  selector: 'hb-indexes',
  templateUrl: './indexes.component.html',
  styleUrls: ['./indexes.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class IndexesComponent implements OnInit, OnDestroy {
  @Input() withGraph = false;
  @Input() orientation: string = ORIENTATIONS.vertical;

  public currentIndex: string; // = 'IPC';
  public indexes: any[] = [];
  public indexData: any[] = [
    {
      name: '',
      series: [],
    },
  ];

  public showGraph = false;
  public currentSelected: any = {};
  public isUsaContract = false;

  private indexSubscription: Subscription;

  private consumerId = 'indexMain';
  private marketName = 'indexes';
  private operationTime: any;
  private mainIndexes: string[] = [
    'IPC',
    'DJI',
    'S&P 500',
    'NASDAQ',
    'BOVESPA',
  ];

  private indexSolaceSubscription: Subscription;

  private index = 0;

  constructor(
    private store: Store<AppState>,
    private bootstrapService: BootstrapService,
    private marketService: MarketService,
    private sharedSolaceStreamService: SharedSolaceStreamService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
  ) {
    this.iconRegistry.addSvgIcon(
      'arrow-issue',
      this.sanitizer.bypassSecurityTrustResourceUrl(
        'images/icons/arrow-right-2.svg',
      ),
    );
  }
  ngOnInit() {
    this.bootstrapService.initSession().then(() => {
      this.store
        .select('marketOperationTime')
        .pipe(
          map((marketOperationTime: any) => marketOperationTime.toJS()),
          take(1),
        )
        .subscribe((marketOperationTime) => {
          this.operationTime = marketOperationTime;
          this.setIndexesData();
        });
    });
    this.listernerIsUsaContract();
  }

  ngOnDestroy() {
    this.disposeIndexesRows();
    this.indexSubscription && this.indexSubscription.unsubscribe();
    this.removeIndexSolaceSubscription();
  }

  public changeCurrentIndex(index: any): void {
    this.indexData = [
      {
        name: '',
        series: [],
      },
    ];
    this.index = this.getIndexPosition(index.name);
    this.currentIndex = index.name;
    this.toggleGraph(true, index);
    this.showGraph && this.withGraph && this.getIndexDataChart();
  }

  public toggleGraph(show: boolean = true, index: any) {
    this.showGraph = show;

    if (this.showGraph) {
      this.currentSelected = index;
    } else {
      this.currentIndex = undefined;
      this.currentSelected = undefined;
    }
  }

  public moveGraphIndex(type: number) {
    const left = 0;
    let indexesCount = 0;

    indexesCount = this.indexes.length - 1;

    if (type === left) {
      if (this.index === 0) {
        this.index = indexesCount;
      } else {
        this.index -= 1;
      }
    } else {
      if (this.index === indexesCount) {
        this.index = 0;
      } else {
        this.index = this.index + 1;
      }
    }
    this.changeCurrentIndex(this.getDataFromIndex(this.index));
  }

  private getIndexPosition(item: string) {
    return this.mainIndexes.indexOf(item);
  }

  private getDataFromIndex(index: number) {
    return this.indexes[index];
  }

  private cleanNumber(value: any): number {
    if (value === undefined) {
      return 0.0;
    }
    if (value.toString().indexOf(',') !== -1) {
      value = value.toString().replace(/,/g, '');
      return parseFloat(value);
    } else {
      return parseFloat(value);
    }
  }

  private formatNumber(value: number): string {
    value = this.cleanNumber(value);

    const COMMAS = 3;
    const DECIMALS = 2 > 0 ? '\\.' : '$';
    const regex = `\\d(?=(\\d{${COMMAS}})+${DECIMALS})`;

    return value.toFixed(2).toString().replace(new RegExp(regex, 'g'), '$&,');
  }

  private removeIndexSolaceSubscription(): void {
    if (this.indexSolaceSubscription) {
      this.indexSolaceSubscription.unsubscribe();
      this.sharedSolaceStreamService.removeConsumerFromObservable(
        this.currentIndex,
        this.marketName,
        this.consumerId,
      );
    }
  }

  private getIndexDataChart(): void {
    const sanitationRules: any = {
      'S&P 500': 'SPX',
    };
    const sanitizedIndex =
      sanitationRules[this.currentIndex] || this.currentIndex;

    const getIndexIntraday$ = this.marketService.getIndexIntraday(
      sanitizedIndex,
      true,
    );

    this.indexSubscription = getIndexIntraday$.subscribe(
      (indexesIntradays) => {
        const tz = 'America/Mexico_City';
        const startOperation = moment(this.operationTime.startTime).tz(tz);
        const endOperation = moment(this.operationTime.endTime).tz(tz);

        startOperation.add(30, 'minutes');

        const filteredIndexes = indexesIntradays
          .filter((item) => {
            const date = moment(item.time).tz(tz).toDate();
            return (
              date >= startOperation.toDate() &&
              date <= endOperation.toDate() &&
              date.getHours()
            );
          })
          .map((element) => {
            const date = moment(element.time).tz(tz).toDate();

            return {
              name: date,
              value: element.value,
            };
          });

        if (this.showGraph) {
          const now = new Date();
          this.indexData[0].name = sanitizedIndex;
          this.indexData[0].series = filteredIndexes;
          now.getHours() < 15 && this.updateDataChartWithSolaces();
        }
      },
      () => {
        this.indexData = [
          {
            name: '',
            series: [],
          },
        ];
      },
    );
  }

  private updateDataChartWithSolaces(): void {
    this.removeIndexSolaceSubscription();

    const marketObservable$: any =
      this.sharedSolaceStreamService.getObservableSymbol(
        this.marketName,
        this.currentIndex,
        this.consumerId,
      );
    if (marketObservable$) {
      this.indexSolaceSubscription = marketObservable$
        .pipe(filter((value: any) => value !== undefined))
        .subscribe((index: any) => {
          const date: any = moment(index.date)
            .tz('America/Mexico_City')
            .toDate();
          this.indexData[0].name = this.currentIndex || '';
          this.indexData[0].series.push({ name: date, value: index.price });
        });
    }
  }

  private disposeIndexesRows(): void {
    for (const index of this.indexes) {
      index.dispose();
    }

    this.indexes.length = 0;
  }

  private setIndexesData(): void {
    this.disposeIndexesRows();

    for (const index of this.mainIndexes) {
      this.indexes.push(new IndexRow(this.sharedSolaceStreamService, index));
    }

    this.changeCurrentIndex(this.indexes[0]);
  }

  private listernerIsUsaContract() {
    this.store
      .select(selectIsUsa)
      .pipe(filter((isUsa) => isUsa !== undefined))
      .subscribe((isUsa: boolean) => {
        this.isUsaContract = isUsa;
      });
  }
}
